import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import { useDebouncedCallback } from 'use-debounce';
import { useTheme } from '@mui/material/styles';
import { useAiChatServices, useAiChatSessionStore } from '../AiChatContext';
import MarkdownExtended from '../../Library/MarkdowmExtended';
import type { Interaction } from '@lib/models/Interaction';
import { Collapse, IconButton, Popover, Paper } from '@mui/material';
import { TipsAndUpdates } from '@mui/icons-material';

interface StickyQuestionHeaderProps {
    messageRefs: React.RefObject<Map<string, HTMLDivElement>>;
    showStickyHeader?: boolean
}

interface QuestionData {
    question: string,
}

const StickyQuestionHeader: React.FC<StickyQuestionHeaderProps> = ({
    messageRefs,
    showStickyHeader
}) => {
    const currentSessionId = useAiChatSessionStore((state) => state.session?.id);
    const loadingType = useAiChatSessionStore((state) => state.loadingType);
    const loading = useAiChatSessionStore((state) => state.loading);
    const messages = useAiChatSessionStore((state) => state.messages);
    const sendingMessage = useAiChatSessionStore((state) => state.sendingMessage);
    const currentMessage = useAiChatSessionStore((state) => state.currentMessage);
    //const [isLandscapeInSmallScreen, setIsLandscapeInSmallScreen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const theme = useTheme();

    const isMobile = theme.breakpoints.down('sm');

    useEffect(() => {
        if (isMobile && window) {
            // const windowWidth = window.innerWidth;
            // const windowHeight = window.innerHeight;
            //setIsLandscapeInSmallScreen(windowWidth > windowHeight && windowHeight < 400);
        }
    }, [isMobile]);

    const { onVisibleQuestionChange } = useAiChatServices();

    const [question, setQuestion] = useState<QuestionData | null>(null);
    const [message] = useState<Interaction | null>(null);
    const [isHeaderExpanded, setIsHeaderExpanded] = useState(false);
    const debouncedSetQuestionInView = useDebouncedCallback((questionData: QuestionData) => {
        setQuestion(questionData);
    }, 10);

    useEffect(() => {
        onVisibleQuestionChange?.(question?.question ?? null);
    }, [question, onVisibleQuestionChange]);

    const maxLines = isHeaderExpanded ? 8 : 2.25;

    const lineHeight = 20;
    const maxHeight = maxLines * lineHeight;

    // Intersection Observer for sticky header
    useEffect(() => {
        const container = document.getElementById('chat-container-' + (currentSessionId ?? 'new'));
        if (!container) {
            return;
        }
        const observer = new IntersectionObserver((entries) => {
            var candidates: { question?: string | null, interactionId: string, agentParts?: string, ratio: number }[] = [];
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const interactionQuestion = entry.target.getAttribute('data-interaction-question');
                    const interactionId = entry.target.getAttribute('data-interactionid');
                    candidates.push({ question: interactionQuestion, interactionId: interactionId ?? '', ratio: entry.intersectionRatio });
                }
            });

            //sort by ratio ascending
            candidates.sort((a, b) => a.interactionId > b.interactionId ? 1 : -1);
            if (candidates.length > 0 && candidates[0].question) {
                debouncedSetQuestionInView({
                    question: candidates[0].question,
                });
            }
        }, {
            threshold: Array.from({ length: 1000 }, (_, i) => i / 1000),
            rootMargin: '-50px 0px -50px 0px', // Account for sticky and user message height
            root: container
        });

        setTimeout(() => {
            let isEmpty = true
            if (messageRefs) {
                messageRefs.current?.forEach((el: HTMLDivElement) => {
                    observer.observe(el)
                    isEmpty = false
                });
            }

            if (isEmpty && !sendingMessage) {
                setQuestion(null);
            }

        }, 500);
    }, [messageRefs.current, currentSessionId, sendingMessage, loading, loadingType, messages, currentMessage]);

    useEffect(() => {
        let lastQuestion: string | null = null;
        if (currentMessage) {
            lastQuestion = currentMessage.interactionQuestion ?? 'Waiting'
        }
        else {
            if (messages && messages.length > 0) {
                lastQuestion = messages[messages.length - 1].interactionQuestion ?? 'Waiting'
            }
        }
        if (lastQuestion) {
            setQuestion({
                question: lastQuestion ?? 'Waiting'
            })
        }
        else {
            setQuestion(null)
        }
    }, [currentMessage, messages, sendingMessage])

    // Check if there are any parts with thoughtDetails
    const hasThoughtDetails = message?.parts.some(part => part.thoughtDetails?.text);
    // Collect all thought details from message parts
    const allThoughtDetails = message?.parts
        .filter(part => part.thoughtDetails?.text)
        .map(part => part.thoughtDetails!.text)
        .join('\n\n---\n\n');

    const open = Boolean(anchorEl);

    const handleTipsClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleTipsClose = () => {
        setAnchorEl(null);
    };



    return question && showStickyHeader ? (
        <Box sx={{
            position: 'relative',
            top: -3,
            left: -19,
            width: 'calc(100% + 36px)',
            zIndex: 100,
            backgroundColor: 'rgba(30, 30, 30,0.8)',
            borderBottom: `1px solid ${theme.palette.divider}`,
            alignContent: 'center',
            backdropFilter: 'blur(2px)',
            transition: 'all 0.5s ease',
            maxWidth: 'calc(100% + 36px)'
        }}>
            <Box sx={{
                position: 'relative',
                maxHeight: `${maxHeight}px`,
                overflow: 'hidden',
                p: 0.5,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                alignContent: 'center',
                verticalAlign: 'middle',
                maxWidth: '100%',
                gap: 1,
                '&::-webkit-scrollbar': {
                    width: '5px',
                    background: theme.palette.background.default,
                },
                '&::-webkit-scrollbar-track': {
                    background: theme.palette.background.default,
                    width: isMobile ? '1px' : '1px',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: theme.palette.background.default,
                    border: '1px solid',
                    width: '5px',
                    borderColor: theme.palette.grey[800],
                    borderRadius: '4px',
                    '&:hover': {
                        background: theme.palette.background.default
                    },
                }
            }}
                onClick={() => setIsHeaderExpanded(!isHeaderExpanded)}
            >
                <Box sx={{
                    overflow: 'hidden',
                    p: 1,
                    marginTop: 1,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'flex-start',
                    alignContent: 'center',
                    verticalAlign: 'middle',
                    gap: 1,
                    mb: 1
                }} ref={messagesEndRef}>
                    <QuestionAnswerIcon
                        sx={{
                            color: 'text.secondary',
                            fontSize: 20,
                            mt: 0,
                            flexShrink: 0
                        }}
                    />

                    <Typography
                        sx={{
                            color: 'text.secondary',
                            fontStyle: 'italic',
                            fontSize: '0.85rem',
                            fontWeight: '400',
                            lineHeight: `${lineHeight}px`,
                            flex: 1
                        }}
                    >
                        {question.question}
                    </Typography>
                </Box>

                {/* Fade effect for collapsed state */}
                {!isHeaderExpanded && (
                    <Box sx={{
                        position: 'absolute',
                        bottom: 0,
                        top: -10,
                        left: -10,
                        height: '24px',
                        background: 'linear-gradient(transparent, rgba(255, 255, 255, 0.14))',
                        pointerEvents: 'none'
                    }} />
                )}

                {/* TipsAndUpdates icon with expand and parallax animation */}
                {message && (
                    <Collapse
                        in={true}
                        orientation="horizontal"
                        timeout={2400}
                        sx={{
                            transformOrigin: 'right center',
                            width: '5%',
                            mb: '10px',
                            '& .MuiCollapse-wrapper': {
                                transform: 'translateX(0)',
                                transition: 'transform 2.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            }
                        }}
                    >
                        {message.turn_complete && hasThoughtDetails && allThoughtDetails && allThoughtDetails.length > 0 && (
                            <IconButton
                                onClick={handleTipsClick}
                                sx={{
                                    color: theme.palette.primary.dark,
                                    ml: 1,
                                    transform: 'scale(1.0)',
                                    transition: 'all 2.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        color: theme.palette.primary.dark,
                                        transform: 'scale(1.2)',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    },
                                    p: 0.3,
                                    '&:active': {
                                        transform: 'scale(1.2)',
                                        transition: 'all 0.1s ease-in-out',
                                    }
                                }}
                            >
                                <TipsAndUpdates
                                    sx={{
                                        fontSize: 22,
                                        transition: 'all 2.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                    }}
                                />
                            </IconButton>
                        )}
                    </Collapse>
                )}

                {/* Popover for thought details */}
                {hasThoughtDetails && allThoughtDetails && <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleTipsClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    PaperProps={{
                        sx: {
                            maxWidth: 600,
                            maxHeight: 400,
                            overflow: 'auto',
                            backgroundColor: 'rgba(0, 0, 0, 0.9)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                        }
                    }}
                >
                    <Paper sx={{ p: 2, backgroundColor: 'transparent' }}>
                        <Typography variant="h6" sx={{ color: 'text.primary', mb: 2, fontWeight: 'bold' }}>
                            AI Thinking Process
                        </Typography>
                        <Box sx={{ color: 'text.primary' }}>
                            <MarkdownExtended
                                sessionId=''
                                text={allThoughtDetails}
                                onLinkClicked={undefined}
                            />
                        </Box>
                    </Paper>
                </Popover>}
            </Box>
        </Box >
    ) : <div style={{ minHeight: '20px' }}></div>;
};

export default StickyQuestionHeader;
